﻿import { mergeUserContexts } from "@/helpers";
import { ISearchSuggestion, IUser } from "@/interfaces";
import {
  appendQueryParameters,
  fetchBaseQueryWrapper,
  NO_CACHING,
} from "@/services/helpers";
import { createApi, retry } from "@reduxjs/toolkit/query/react";

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: retry(
    fetchBaseQueryWrapper({
      path: "users",
      extraOptions: { maxRetries: 0 },
    })
  ),
  keepUnusedDataFor: NO_CACHING,
  endpoints: (build) => ({
    getUserToken: build.query({
      query: (id) => ({
        url: `token/${id || ""}`,
      }),
      extraOptions: { maxRetries: 2 },
      transformResponse: ({ data, data: { user } }) => {
        const mergedContext = mergeUserContexts(user);

        if (!mergedContext) return data;

        return {
          ...data,
          businessUnit: mergedContext.metadata?.businessUnit,
          rights: mergedContext.roles,
        };
      },
    }),
    getUser: build.query({
      query: (id) => id,
      transformResponse: ({ data }) => data,
    }),
    getUsersByIds: build.mutation({
      query: (ids) => ({
        url: "",
        method: "POST",
        body: {
          userIds: ids,
        },
      }),
      transformResponse: ({ data }) => data,
    }),
    getUserPhoto: build.query({
      query: (id) => `${id}/photo`,
      transformResponse: ({ data }) => data,
    }),
    searchUsers: build.query({
      query: (queryParameters) =>
        appendQueryParameters("search", queryParameters),
      transformResponse: ({
        data: { azureUsers: users },
        totalCount,
        totalPages,
        count,
      }) => ({
        users,
        totalCount,
        totalPages,
        count,
      }),
    }),
    suggestUsers: build.query<
      ISearchSuggestion<IUser>[],
      { query: string; viperOnly?: boolean; additionalParameters?: object }
    >({
      query: ({ query, viperOnly = true, additionalParameters = {} }) =>
        `suggestions?${new URLSearchParams({
          query,
          viperUsersOnly: viperOnly.toString(),
          ...additionalParameters,
        })}`,
      transformResponse: ({ data }) => data,
    }),
    hasInactiveUsers: build.mutation({
      query: (ids) => ({
        url: "inactive",
        method: "POST",
        body: {
          userIds: ids,
        },
      }),
      transformResponse: ({ data }) => data,
    }),
    toggleHideViperUser: build.mutation({
      query: (id) => ({
        url: `${id}/toggleHide`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetUserTokenQuery,
  useGetUserQuery,
  useGetUsersByIdsMutation,
  useGetUserPhotoQuery,
  useSearchUsersQuery,
  useSuggestUsersQuery,
  useToggleHideViperUserMutation,
  useHasInactiveUsersMutation,
} = userApi;
